import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAccessRight, useTranslate } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotId, selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import {
	addConnector,
	addCurrentConnector,
	deleteConnector,
	editConnector,
	getConnectorList,
	selectAddingConnector,
	selectConnectorList,
	selectDeletingConnector,
	selectEditingConnector,
} from "@/store/ses/sesConnectorSlice";
import useUncommittedRobotsList from "@/hooks/useUncommittedRobotsList";
import { SES } from "@/constants/accessRights";
import { colorSecondary } from "@/constants/colors";
import { CONNECTOR_FREESWITCH, CONNECTOR_MAX, CONNECTOR_NATS, CONNECTOR_TELEGRAM, CONNECTOR_VK, CONNECTOR_WEB } from "@/constants/defaultValues";
import {
	IConnectorFreeswitchAssistant,
	IConnectorFreeswitchRobot,
	IConnectorNats,
	IConnectorWebAssistant,
	IConnectorWebChat,
	TChannel,
	TConnectors,
} from "@/types/ses/sesConnectorTypes";
import { RequestStatus, ResponseStatus } from "@/types/statusTypes";
import CustomTextField from "@/components/MUI/CustomTextField/CustomTextField";
import CustomMenuItem from "@/components/MUI/CustomMenuItem/CustomMenuItem";
import CustomButton from "../MUI/CustomButton/CustomButton";
import { AlertDialog, BlockWithError, BlockWithInfo, BlockWithLoading } from "@/ui";
import Messenger from "./Messenger/Messenger";
import Web from "./Web/Web";
import Freeswitch from "./Freeswitch/Freeswitch";
import Nats from "./Nats/Nats";
import { TMessengerConnectorData } from "./Messenger/Messenger.props";
import { IChangeFlg, NamesOfChangedKeys } from "./Connectors.props";
import styles from "./Connectors.module.scss";
import { Hint } from "@/HOC";

// получение дефолтных данных по каналу
const getDefaultDataByChannel = (channel: TChannel): TConnectors["params"] => {
	switch (channel) {
		case "telegram":
			return CONNECTOR_TELEGRAM;
		case "max":
			return CONNECTOR_MAX;
		case "vk":
			return CONNECTOR_VK;
		case "web":
			return CONNECTOR_WEB;
		case "freeswitch":
			return CONNECTOR_FREESWITCH;
		case "nats":
			return CONNECTOR_NATS;
		default:
			const never = channel;
			return never;
	}
};

const Connectors = (): JSX.Element => {
	const [selectChannel, setSelectChannnel] = useState<TChannel>("telegram"); // канал
	const [selectId, setSelectId] = useState<string>(""); // id коннектора
	const [showAlertDialogDelConnector, setShowAlertDialogDelConnector] = useState<boolean>(false); // показ диалогового окна при удалении коннектора
	const [changeFlg, setChangeFlg] = useState<IChangeFlg>({ thisIs: false, listOfChanges: [] }); // флаг, уведомляющий об изменении данных и возможности сохранить эти изменения

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const connectors = useAppSelector(selectConnectorList); // store - коннекторы
	const addingConnector = useAppSelector(selectAddingConnector); // store - статус добавления коннектора
	const editingConnector = useAppSelector(selectEditingConnector); // store - статус изменения коннектора
	const deletingConnector = useAppSelector(selectDeletingConnector); // store - статус удаления коннектора
	const uncommittedRobotsList = useUncommittedRobotsList(); // hook для обработки списка незакоммиченных настроек роботов

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// обработчик установки флага изменений
	const changeFlgHandler = useCallback(
		(changed: NamesOfChangedKeys): void => {
			if (!changeFlg.thisIs || !changeFlg.listOfChanges.includes(changed)) {
				setChangeFlg((prev) => ({ thisIs: true, listOfChanges: [...prev.listOfChanges, changed] })); // ставим флаг о несохраненных данных
			}
		},
		[changeFlg]
	);

	// следим за id робота и версией
	useEffect(() => {
		setSelectId(""); // сбрасываем id коннектора
	}, [activeRobotId, activeRobotVersion]);

	// следим за выбранным каналом, списком коннекторов - выбираем id коннектора
	useEffect(() => {
		// новый добавленный коннектор
		if (addingConnector.id) {
			setSelectId(addingConnector.id);
		} else if (!selectId) {
			// поиск коннектора по каналу
			const connectorByChannel = connectors.data.find((connector) => connector.type === selectChannel);
			if (connectorByChannel) setSelectId(connectorByChannel.id);
			else setSelectId("");
		}
	}, [selectChannel, connectors.data]);

	// следим за id коннектора и устанавливаем текущий коннектор
	useEffect(() => {
		if (selectId) {
			const connectorById = connectors.data.find((connector) => connector.id === selectId);
			dispatch(addCurrentConnector(connectorById || null));
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
		} else {
			dispatch(addCurrentConnector(null));
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
		}
	}, [selectId]);

	// следим за статусом добавления коннектора
	useEffect(() => {
		// если успешно
		if (addingConnector.status === RequestStatus.IDLE && addingConnector.error === ResponseStatus.SUCCESS && addingConnector.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			setSelectId(""); // сбрасываем id
			isAccess(SES.CONNECTOR_LIST) && activeRobotId && dispatch(getConnectorList(activeRobotId)); // обновляем список коннекторов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [addingConnector]);

	// следим за статусом изменения коннектора
	useEffect(() => {
		// если успешно
		if (editingConnector.status === RequestStatus.IDLE && editingConnector.error === ResponseStatus.SUCCESS && editingConnector.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			isAccess(SES.CONNECTOR_LIST) && activeRobotId && dispatch(getConnectorList(activeRobotId)); // обновляем список коннекторов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [editingConnector]);

	// следим за статусом удаления коннектора
	useEffect(() => {
		// если успешно
		if (deletingConnector.status === RequestStatus.IDLE && deletingConnector.error === ResponseStatus.SUCCESS && deletingConnector.message !== "") {
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			setSelectId(""); // сбрасываем id
			isAccess(SES.CONNECTOR_LIST) && activeRobotId && dispatch(getConnectorList(activeRobotId)); // обновляем список коннекторов
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [deletingConnector]);

	// обработчик добавления коннектора
	const connectorAddHandler = (): void => {
		const connectorsByChannel = connectors.data.filter((el) => el.type === selectChannel);
		let connectorName = `connector_${connectorsByChannel.length + 1}`;
		if (connectorsByChannel.some((conn) => conn.name === connectorName)) connectorName += `_${uuidv4().slice(0, 5)}`; // на случай существующих имён
		if (activeRobotId) {
			dispatch(addConnector({ robotId: activeRobotId, connectorName, channel: selectChannel, params: getDefaultDataByChannel(selectChannel) }));
		}
	};

	// обработчик сохранения коннектора
	const connectorSaveHandler = (): void => {
		if (connectors.currentConnector && activeRobotId) {
			dispatch(
				editConnector({
					robotId: activeRobotId,
					connectorId: selectId,
					name: connectors.currentConnector.name,
					params: connectors.currentConnector.params,
				})
			); // сохраняем изменения
		}
	};

	// обработчик удаления коннектора
	const connectorDeleteHandler = (): void => {
		if (activeRobotId) {
			dispatch(deleteConnector({ robotId: activeRobotId, connectorId: selectId }));
		}
	};

	return (
		<div className={styles.container}>
			{/* загрузка списка коннекторов */}
			{connectors.status === RequestStatus.LOADING && <BlockWithLoading />}

			{/* ошибка получения списка коннекторов */}
			{connectors.status === RequestStatus.FAILED && (
				<BlockWithError message={connectors.message} updateHandler={() => activeRobotId && dispatch(getConnectorList(activeRobotId))} />
			)}

			{/* коннекторы */}
			{connectors.status === RequestStatus.IDLE && (
				<div className={styles.connector}>
					<div className={styles.block}>
						{/* канал */}
						<Hint hint={"hint_connectorChannel"} width="100%" position="top">
							<CustomTextField
								fullWidth
								select
								label={translate("select_channel")}
								value={selectChannel}
								onChange={(e) => {
									setSelectId(""); // сброс id
									setSelectChannnel(e.target.value as TChannel);
								}}
							>
								{Object.entries(
									connectors.data.reduce(
										(acc, el) => {
											acc[el.type] = (acc[el.type] || 0) + 1;
											return acc;
										},
										{ telegram: 0, max: 0, vk: 0, web: 0, freeswitch: 0, nats: 0 } as Record<TChannel, number>
									)
								).map(([channel, count]) => (
									<CustomMenuItem key={channel} value={channel}>
										{`${channel} ${!count ? "" : `(${count})`}`}
									</CustomMenuItem>
								))}
							</CustomTextField>
						</Hint>

						{/* id коннектора */}
						<Hint hint={translate("hint_connectorConnector")} width="100%" position="top">
							<CustomTextField fullWidth select label={translate("select_connector")} value={selectId} onChange={(e) => setSelectId(e.target.value)}>
								{isAccess(SES.CONNECTOR_ADD) && activeRobotVersion !== "backup" && (
									<CustomMenuItem onClick={connectorAddHandler} sx={{ color: colorSecondary, fontWeight: "bold" }}>
										{translate("selectItem_addConnector")}
									</CustomMenuItem>
								)}
								{connectors.data
									.filter((connector) => connector.type === selectChannel)
									.map((connector) => (
										<CustomMenuItem key={connector.id} value={connector.id}>
											{connector.name}
										</CustomMenuItem>
									))}
							</CustomTextField>
						</Hint>
					</div>

					<div className={styles.content}>
						{!connectors.currentConnector ? (
							<BlockWithInfo title="title_empty" />
						) : (
							<>
								{((selectChannel === "max" && connectors.currentConnector.type === "max") ||
									(selectChannel === "telegram" && connectors.currentConnector.type === "telegram") ||
									(selectChannel === "vk" && connectors.currentConnector.type === "vk")) && (
									<Messenger
										selectId={selectId}
										data={{ channel: selectChannel, settings: { ...connectors.currentConnector.params } } as TMessengerConnectorData}
										connectorName={connectors.currentConnector.name}
										changeFlgHandler={changeFlgHandler}
									/>
								)}
								{selectChannel === "web" && connectors.currentConnector.type === "web" && (
									<Web
										selectId={selectId}
										channel={selectChannel}
										settings={connectors.currentConnector.params as IConnectorWebChat | IConnectorWebAssistant}
										connectorName={connectors.currentConnector.name}
										changeFlgHandler={changeFlgHandler}
									/>
								)}
								{selectChannel === "freeswitch" && connectors.currentConnector.type === "freeswitch" && (
									<Freeswitch
										selectId={selectId}
										channel={selectChannel}
										connectorName={connectors.currentConnector.name}
										settings={connectors.currentConnector.params as IConnectorFreeswitchRobot | IConnectorFreeswitchAssistant}
										changeFlgHandler={changeFlgHandler}
									/>
								)}
								{selectChannel === "nats" && connectors.currentConnector.type === "nats" && (
									<Nats
										selectId={selectId}
										channel={selectChannel}
										connectorName={connectors.currentConnector.name}
										settings={connectors.currentConnector.params as IConnectorNats}
										changeFlgHandler={changeFlgHandler}
									/>
								)}
							</>
						)}
					</div>

					{activeRobotVersion !== "backup" && (
						<div className={styles.block}>
							{/* сохранение коннектора */}
							{isAccess(SES.CONNECTOR_MODIFY) && (
								<Hint hint={"hint_connectorsSaveButton"} width="100%" position="top">
									<CustomButton fullWidth disabled={!changeFlg.thisIs} onClick={connectorSaveHandler}>
										{translate("button_save")}
									</CustomButton>
								</Hint>
							)}
							{/* удаление коннектора */}
							{isAccess(SES.CONNECTOR_DELETE) && (
								<Hint hint={"hint_connectorsDeleteButton"} width="100%" position="top">
									<CustomButton fullWidth disabled={!connectors.currentConnector} onClick={() => setShowAlertDialogDelConnector(true)} color="error">
										{translate("button_delete")}
									</CustomButton>
								</Hint>
							)}
						</div>
					)}
				</div>
			)}

			{/* подтверждение удаления коннектора */}
			<AlertDialog
				showAlertDialog={showAlertDialogDelConnector}
				setShowAlertDialog={setShowAlertDialogDelConnector}
				submitHandler={connectorDeleteHandler}
				title="dialog_deleteConnector"
				description="dialog_deleteConnectorConfirm"
				name={connectors.currentConnector?.name}
			/>
		</div>
	);
};

export default Connectors;
